import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Container } from '../../containers/container';
import { Group } from '../../shared/entities/group/group';
import { Location } from '../../shared/entities/location/location';
import { Role } from '../../shared/entities/role/role';
import { User } from '../../shared/entities/user/user';
import { LoadingService } from '../../shared/services/loading.service';
import { LocationService } from '../../shared/services/location/location.service';
import { RolesService } from '../../shared/services/roles/roles.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { UsersService } from '../../shared/services/users/users.service';
import { AddRolesDialogComponent } from '../add-roles-dialog/add-roles-dialog.component';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { EditDialogService } from '../editDialog/editDialogService';
import { UserDialogComponent } from '../user-dialog/user-dialog.component';
import { UserLicenceDialogComponent } from '../user-licence-dialog/user-licence-dialog.component';
import { UserPasswordDialogComponent } from '../user-password-dialog/user-password-dialog.component';
import * as fromGraphQl from './graphql';


interface Setting {
  key: string;
  value: any;
}

@Component({
  selector: 'app-user-view',
  templateUrl: './user-view.component.html',
  styleUrls: ['./user-view.component.scss']
})
export class UserViewComponent
  extends Container<fromGraphQl.UserQueryResponse>
  implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public user: User;
  public id: string;
  public routeSubscription: Subscription;
  public UserClass = User;
  public locations: Location[];
  public availableRoles: Role[];
  public usersRoles: Role[];
  public usersRolesFiltered: Role[];

  public userGroups: Group[];
  public selectedRoles: Role[];

  isRowSelectableFunction = this.isRowSelectable.bind(this);

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    loadingService: LoadingService,
    apollo: Apollo,
    public dialogService: EditDialogService,
    private confirmDialogService: ConfirmDialogService,
    @Inject('env') environment : any,
    private userService: UsersService,
    private toastService: ToastService,
    private locationService: LocationService,
    private rolesService: RolesService,
    private keycloakAngular: KeycloakService,
  ) {
    super(apollo, loadingService, fromGraphQl.USERS_QUERY, environment);
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params
      .pipe(
        filter((params) => params['id'] !== undefined),
        take(1)
      )
      .subscribe((params) => {
        this.id = params['id'];
        this.subscribeToQuery({
          id: this.id,
        });
      });
    this.locationService.getLocations().pipe(take(1)).subscribe((locations) => {
      this.locations = locations;
    });
    this.rolesService.getRoles().pipe(take(1)).subscribe((roles) => {
      this.availableRoles = roles;
      this.filterUserRoles();
      this.filterUserGroups();
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  handleSubscriptionResult(data: fromGraphQl.UserQueryResponse): void {
    this.loadingService.setLoading(false);
    if (data) {
      this.queryRef.stopPolling();
      if (data.user) {
        this.user = new User(data.user);
        this.filterUserRoles();
      }
    }
  }


  editUser(): void {
    this.dialogRef = this.dialogService.open(UserDialogComponent, {
      header: 'Nutzer bearbeiten',
      data: this.user
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if(result){
        this.queryRef.refetch();
      }
    });
  }

  editLicence(): void {
    this.dialogRef = this.dialogService.open(UserLicenceDialogComponent, {
      header: 'Lizenz bearbeiten',
      data: this.user
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if(result){
        this.queryRef.refetch();
      }
    });
  }

  suspendUser(): void {
    const message = 'Möchtest Du den Nutzer ' + this.user.firstName + ' ' + this.user.lastName + ' wirklich deaktivieren?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Nutzer deaktivieren'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.userService.suspendUser(this.user.id, () => {
            this.queryRef.refetch();
            this.loadingService.setLoading(false);
            this.toastService.addToastWithMessage('Nutzer deaktiviert.');
          });
      };
    })
  }

  enableUser(): void {
    const message = 'Möchtest Du den Nutzer ' + this.user.firstName + ' ' + this.user.lastName + ' wirklich aktivieren?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.DEFAULT,
      header: 'Nutzer aktivieren'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.userService.enableUser(this.user.id, () => {
            this.queryRef.refetch();
            this.loadingService.setLoading(false);
            this.toastService.addToastWithMessage('Nutzer aktiviert.');
          });
      };
    })
  }

  resetPassword(): void {
    this.dialogRef = this.dialogService.open(UserPasswordDialogComponent, {
      header: 'Passwort zurücksetzen',
      data: this.user
    });
    this.dialogRef.onClose.subscribe( (result) => {
      // result is true or false, indiciating if it should refetch
      // nothing special to do here
      if(result){
        this.toastService.addToastWithMessage('Passwort zurückgesetzt.');
      }
    });
  }

  getUserLocations(): string {
    // get the location ids of the user an map them to the location names
    // if it cannot find locations, skip them

    let assignedLocations = this.user?.locationIds?.map((locationId) => {
      const location = this.locations?.find((location) => location.id === locationId);
      return location?.name;
    });
    // filter all undefined values from array
    return assignedLocations?.filter((location) => location !== undefined).join(', ');
  }

  filterUserRoles(): void {
    if (this.user && this.availableRoles) {
      this.usersRoles = this.user.roles.filter((role) => {
        return this.availableRoles.find((availableRole) => availableRole.id === role.id);
      });
      this.usersRolesFiltered = this.usersRoles;
    }
  }

  filterUserGroups(): void {
    // we want to filter out the tenant group
    let tenantId = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['tenant_id']
      : '';
    if (this.user && this.user.groups) {
      this.userGroups = this.user.groups.filter((group) => {
        return group.name !== tenantId;
      });
    }
  }

  removeRole(role: Role): void {
    const message = 'Möchtest Du die Rolle ' + role.label + ' wirklich entfernen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Rolle entfernen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.userService.removeRoles(this.user, [role], () => {
          this.queryRef.refetch();
          this.toastService.addToastWithMessage('Rolle entfernt.');
        });
      };
    })
    // do nothing
  }

  removeRoles(): void {
    const message = 'Möchtest Du ' + this.selectedRoles?.length + ' Rolle(n) wirklich entfernen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Rollen entfernen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.userService.removeRoles(this.user, this.selectedRoles, () => {
          this.queryRef.refetch();
          this.selectedRoles = undefined;
          this.toastService.addToastWithMessage('Rollen entfernt.');
        });
      };
    })
    // do nothing
  }

  addRoles(): void {
    this.dialogRef = this.dialogService.open(AddRolesDialogComponent, {
      header: 'Rollen hinzufügen',
      data: {
        assignedRoles: this.user.roles,
        availableRoles: this.availableRoles,
      }
    });
    this.dialogRef.onClose.subscribe((result) => {
      if(result){
        this.loadingService.setLoading(true);
        this.userService.addRoles(this.user, result, () => {
          this.queryRef.refetch();
          this.toastService.addToastWithMessage('Rolle(n) hinzugefügt.');
        });
       };
    });
  }

  isRowSelectable(event: any): boolean {
    return !event.data.isInherited;
  }

  loadData(event: TableLazyLoadEvent) {
    //this.loading = true;

    // sorting

    //filtering
    let normalizedSearchTerm = (event.globalFilter as string)?.toLowerCase();

    // pagination
    if (normalizedSearchTerm) {
      this.usersRolesFiltered = this.usersRoles.filter((role) =>
        role.label?.toLowerCase().includes(normalizedSearchTerm)
      );
    }
    else {
      this.usersRolesFiltered = this.usersRoles;
    }


  }
}
